import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, ArrowRight, X } from "lucide-react";

// Global "Free trial for the day expired" toast — appears top-center, Rolex-green
// bordered card, auto-dismisses after 10 seconds. Triggered by axios interceptor
// on any 402 { code: "limit_reached" }.
// - Signed-in free users see only "Upgrade to Pro"
// - Signed-out users see "Sign in" + "Upgrade to Pro"
export default function LimitReachedModal() {
  const [open, setOpen] = useState(false);
  const [info, setInfo] = useState(null);
  const timerRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const onEvent = (e) => {
      setInfo(e.detail || {});
      setOpen(true);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setOpen(false), 10000);
    };
    window.addEventListener("buddy:limit-reached", onEvent);
    return () => {
      window.removeEventListener("buddy:limit-reached", onEvent);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const close = () => { if (timerRef.current) clearTimeout(timerRef.current); setOpen(false); };
  const goUpgrade = () => { close(); navigate("/pricing"); };
  const goLogin   = () => { close(); navigate("/login"); };
  if (!info) return null;
  const signedIn = !!info.signedIn;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0, y: -30, x: "-50%" }}
          animate={{ opacity: 1, y: 0, x: "-50%" }}
          exit={{ opacity: 0, y: -20, x: "-50%" }}
          transition={{ type: "spring", stiffness: 260, damping: 24 }}
          className="fixed left-1/2 top-6 z-[9999] w-full max-w-md px-4 pointer-events-auto"
          data-testid="limit-reached-modal"
        >
          <div
            className="relative rounded-2xl p-5 shadow-xl"
            style={{
              background: "var(--surface-white, #ffffff)",
              border: "2px solid var(--rolex-green, #006039)",
              boxShadow: "0 12px 40px rgba(0,96,57,0.25)",
            }}
          >
            <button
              onClick={close}
              className="absolute top-2 right-2 p-1.5 rounded-full transition"
              style={{ color: "var(--text-muted)" }}
              aria-label="Close"
              data-testid="limit-modal-close"
            >
              <X size={16} />
            </button>
            <div className="flex items-center gap-2 mb-2">
              <Sparkles size={16} style={{ color: "var(--rolex-green, #006039)" }} />
              <h3 className="font-display text-base font-semibold" style={{ color: "var(--rolex-green, #006039)" }}>
                Daily limit reached
              </h3>
            </div>
            <p className="text-sm leading-relaxed mb-4" style={{ color: "var(--text-secondary)" }} data-testid="limit-modal-message">
              {info.message || `You've reached your daily ${info.toolLabel || "rewrite"} limit. Please upgrade to continue.`}
            </p>
            <div className="flex gap-2">
              <button
                onClick={goUpgrade}
                className="btn-primary py-2 px-4 text-sm flex items-center justify-center gap-1.5 flex-1"
                data-testid="limit-modal-upgrade-btn"
              >
                Upgrade to Pro <ArrowRight size={14} />
              </button>
              {!signedIn && (
                <button
                  onClick={goLogin}
                  className="py-2 px-4 text-sm rounded-lg border transition"
                  style={{ borderColor: "var(--rolex-green, #006039)", color: "var(--rolex-green, #006039)", background: "transparent" }}
                  data-testid="limit-modal-signin-btn"
                >
                  Sign in
                </button>
              )}
            </div>
            {/* Auto-dismiss timer track (10s) */}
            <div className="mt-3 h-1 rounded-full overflow-hidden" style={{ background: "rgba(0,96,57,0.10)" }}>
              <motion.div
                key={open ? "run" : "idle"}
                initial={{ width: "100%" }}
                animate={{ width: "0%" }}
                transition={{ duration: 10, ease: "linear" }}
                style={{ height: "100%", background: "var(--rolex-green, #006039)" }}
              />
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
